import {
  SHARED_PATRON_ASSET_METADATA,
  type AssetGroupId,
  extractAssetGroupId,
} from '@infinex/asset-config';
import { styleVars } from '@infinex/ui-styles';
import { type EmblemProps, Emblem } from '@infinex/ui/components';

import { useAssetMetadataQuery } from '../../api/assets/assetQueries';

type CommonProps = {
  /**
   * Use when an asset is available on multiple chains to indicate the specific
   * chain associated with the asset.
   */
  chainAdornment?: EmblemProps['chain'];
} & Pick<EmblemProps, 'size' | 'className' | 'style' | 'asset'>;
export type AssetIconProps = CommonProps & AssetGroupId;

/**
 * An asset icon is a symbolic image that serves as a graphic representation of
 * a "token" asset.
 */
export const AssetIcon = (props: AssetIconProps) => {
  const { data: metadata, isPending: isMetadataLoading } =
    useAssetMetadataQuery(extractAssetGroupId(props));

  return (
    <AssetIconUI
      isLoading={isMetadataLoading}
      imgUrl={metadata?.imgUrl}
      symbol={metadata?.symbol}
      {...props}
    />
  );
};

export const AssetIconUI = (
  props: AssetIconProps & {
    isLoading?: boolean;
    imgUrl: string | undefined;
    symbol: string | undefined;
  }
) => {
  const { chainAdornment, className, style, size = 'large' } = props;
  const styleProps = { className, style, size };

  if (props.isLoading) {
    return <Emblem alt="" chain={chainAdornment} {...styleProps} />;
  }

  let src = props.imgUrl;
  const isPatronNft =
    props.chainAdornment === 'ethereum' &&
    (props as any).assetId === 'patron_nft';

  if (isPatronNft) {
    // special case for patron nft
    src = SHARED_PATRON_ASSET_METADATA.imageUrl;
  }

  return (
    <Emblem
      alt={props.symbol ?? ''}
      src={src}
      chain={chainAdornment}
      asset={isPatronNft ? 'nft' : props.asset}
      className={className}
      style={{
        ...styleVars({
          '--radius': isPatronNft ? '8px' : undefined,
        }),
        ...style,
      }}
      size={size}
    />
  );
};
